import React, { useEffect, useState, useRef, useMemo } from "react";
import AdminLayout from "./Common/AdminLayout";
import { useDispatch, useSelector } from "react-redux";
import { FetchUsers } from "../../Redux/Slices/User";
import {
  CreateSubscriptionByAdmin,
  GetOnePackage,
  GetPackage,
} from "../../Redux/Slices/Package";
import "../../asset/new/css/admin-modern.css";

const AddAccess = () => {
  const dispatch = useDispatch();
  const { usersData } = useSelector((state) => state?.User);
  const dropdownRef = useRef(null);
  const packageDropdownRef = useRef(null);

  // State management
  const [formData, setFormData] = useState({
    package: "",
    remarks: ""
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [packageDetails, setPackageDetails] = useState([]);
  const [purchase, setPurchase] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showPackageDropdown, setShowPackageDropdown] = useState(false);
  const [packageSearchTerm, setPackageSearchTerm] = useState("");
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Memoized filtered users
  const jobRecruiterUsers = useMemo(() =>
    usersData?.docs?.filter(user => user?.role_id?.name === "Employer") || []
  , [usersData]);

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return jobRecruiterUsers;
    return jobRecruiterUsers.filter(user =>
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, jobRecruiterUsers]);

  // Filtered active packages with search
  const filteredPackages = useMemo(() => {
    const activePackages = packageDetails.filter(item => item?.is_active);
    if (!packageSearchTerm) return activePackages;
    return activePackages.filter(pkg => 
      pkg.name.toLowerCase().includes(packageSearchTerm.toLowerCase())
    );
  }, [packageDetails, packageSearchTerm]);

  // Initial data fetch
  useEffect(() => {
    const fetchData = async () => {
      try {
        const userData = { role: "Employer", alllist: "all" };
        await dispatch(FetchUsers(userData));

        const pkgData = await GetPackage();
        setPackageDetails(pkgData?.docs || []);

        document.title = "Add Access | Admin";
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };

    fetchData();
  }, [dispatch]);

  // Package details fetch
  useEffect(() => {
    const fetchPackageDetails = async () => {
      if (!formData.package) return;
      try {
        const data = await GetOnePackage(formData.package);
        setPurchase(data);
      } catch (error) {
        console.error("Error fetching package details:", error);
      }
    };

    fetchPackageDetails();
  }, [formData.package]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (packageDropdownRef.current && !packageDropdownRef.current.contains(event.target)) {
        setShowPackageDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUserSelect = (user) => {
    setSelectedUser({
      value: user.email,
      label: user.email,
      id: user._id
    });
    setSearchTerm(user.email);
    setShowDropdown(false);
  };

  const handlePackageSelect = (pkg) => {
    setFormData(prev => ({ ...prev, package: pkg._id }));
    setSelectedPackage(pkg);
    setPackageSearchTerm(pkg.name);
    setShowPackageDropdown(false);
  };

  const clearPackageSelection = () => {
    setFormData(prev => ({ ...prev, package: "" }));
    setSelectedPackage(null);
    setPackageSearchTerm("");
    setPurchase(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedUser) {
      alert("Please select an employer");
      return;
    }

    if (!formData.package) {
      alert("Please select a package");
      return;
    }

    try {
      const subscriptionData = {
        payment_id: formData.remarks,
        package: formData.package,
        employer: selectedUser.id,
        avail_jobposting: purchase?.no_job_posting,
        avail_cvsearch: purchase?.no_cv_search,
        validity_day: purchase?.validity_day,
      };

      await CreateSubscriptionByAdmin(subscriptionData);
      // Reset form after successful submission
      setFormData({ package: "", remarks: "" });
      setSelectedUser(null);
      setSearchTerm("");
      setPackageSearchTerm("");
      setSelectedPackage(null);
      setPurchase(null);
      alert("Subscription created successfully!");
    } catch (error) {
      console.error("Error creating subscription:", error);
      alert("Failed to create subscription");
    }
  };

  return (
    <AdminLayout
      title="Add Access"
      subtitle="Grant package access to employers"
    >
      <div className="admin-card">
        <div className="admin-card-header">
          <h4 className="admin-card-title">Create Subscription</h4>
        </div>
        <div className="admin-card-body">
          <form onSubmit={handleSubmit}>
            <div className="row g-4">
              {/* Employer Search */}
              <div className="col-md-6">
                <label className="form-label">
                  <i className="fas fa-user-tie me-2"></i>
                  Select Employer
                </label>
                <div className="custom-select-wrapper" ref={dropdownRef}>
                  <div className="custom-select-container">
                    <input
                      type="text"
                      className="admin-input"
                      placeholder="Search employer by email..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setShowDropdown(true);
                      }}
                      onFocus={() => setShowDropdown(true)}
                      required
                    />
                    <div className="position-absolute end-0 top-50 translate-middle-y pe-3" style={{ pointerEvents: "none" }}>
                      <i className="fas fa-chevron-down text-muted"></i>
                    </div>
                  </div>
                  {showDropdown && (
                    <div className="custom-select-dropdown">
                      {filteredUsers.length > 0 ? (
                        filteredUsers.map((user) => (
                          <div
                            key={user._id}
                            className="custom-select-option"
                            onClick={() => handleUserSelect(user)}
                          >
                            <i className="fas fa-user me-2"></i>
                            {user.email}
                          </div>
                        ))
                      ) : (
                        <div className="custom-select-option no-results">
                          <i className="fas fa-info-circle me-2"></i>
                          No employers found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Package Selection */}
              <div className="col-md-6">
                <label className="form-label">
                  <i className="fas fa-box me-2"></i>
                  Select Package
                </label>
                <div className="custom-select-wrapper" ref={packageDropdownRef}>
                  <div className="custom-select-container">
                    {selectedPackage ? (
                      <div className="selected-package-container">
                        <span className="selected-package-name">
                          <i className="fas fa-check-circle text-success me-2"></i>
                          {selectedPackage.name}
                        </span>
                        <button
                          type="button"
                          className="btn-close-package"
                          onClick={clearPackageSelection}
                          aria-label="Clear selection"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="text"
                          className="admin-input"
                          placeholder="Search package..."
                          value={packageSearchTerm}
                          onChange={(e) => {
                            setPackageSearchTerm(e.target.value);
                            setShowPackageDropdown(true);
                          }}
                          onFocus={() => setShowPackageDropdown(true)}
                          required
                        />
                        <div className="position-absolute end-0 top-50 translate-middle-y pe-3" style={{ pointerEvents: "none" }}>
                          <i className="fas fa-chevron-down text-muted"></i>
                        </div>
                      </>
                    )}
                  </div>
                  {showPackageDropdown && (
                    <div className="custom-select-dropdown">
                      {filteredPackages.length > 0 ? (
                        filteredPackages.map((pkg) => (
                          <div
                            key={pkg._id}
                            className="custom-select-option"
                            onClick={() => handlePackageSelect(pkg)}
                          >
                            <i className="fas fa-gift me-2"></i>
                            {pkg.name}
                          </div>
                        ))
                      ) : (
                        <div className="custom-select-option no-results">
                          <i className="fas fa-info-circle me-2"></i>
                          No packages found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Package Details */}
            {purchase && (
              <>
                <hr className="my-4" />
                <h5 className="mb-3">
                  <i className="fas fa-info-circle me-2 text-primary"></i>
                  Package Details
                </h5>
                <div className="row g-4">
                  <div className="col-md-3">
                    <label className="form-label">Package Type</label>
                    <input
                      type="text"
                      className="admin-input"
                      value={purchase?.name || ""}
                      disabled
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Job Postings</label>
                    <input
                      type="text"
                      className="admin-input"
                      value={purchase?.no_job_posting || ""}
                      disabled
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">CV Searches</label>
                    <input
                      type="text"
                      className="admin-input"
                      value={purchase?.no_cv_search || ""}
                      disabled
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Validity</label>
                    <input
                      type="text"
                      className="admin-input"
                      value={purchase?.validity_day ? `${purchase.validity_day} Days` : ""}
                      disabled
                    />
                  </div>
                </div>
              </>
            )}

            {/* Remarks */}
            <div className="row g-4 mt-2">
              <div className="col-md-12">
                <label className="form-label">Remarks (Optional)</label>
                <textarea
                  name="remarks"
                  onChange={handleInputChange}
                  value={formData.remarks}
                  className="admin-input"
                  placeholder="Enter any additional remarks or payment ID..."
                  rows="3"
                />
              </div>
            </div>

            <div className="text-center mt-4">
              <button
                type="submit"
                className="admin-btn admin-btn-primary admin-btn-lg"
              >
                <i className="fas fa-check me-2"></i>
                Create Subscription
              </button>
            </div>
          </form>
        </div>
      </div>

      <style jsx>{`
        .custom-select-wrapper {
          position: relative;
          width: 100%;
        }
        
        .custom-select-container {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .custom-select-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          z-index: 1000;
          max-height: 250px;
          overflow-y: auto;
          background: white;
          border: 1px solid var(--admin-border);
          border-radius: var(--radius-md);
          margin-top: 4px;
          box-shadow: var(--shadow-lg);
        }
        
        .custom-select-option {
          padding: 12px 16px;
          cursor: pointer;
          transition: all var(--transition-base);
          border-bottom: 1px solid var(--admin-border-light);
        }
        
        .custom-select-option:last-child {
          border-bottom: none;
        }
        
        .custom-select-option:hover {
          background-color: var(--admin-primary);
          color: white;
        }
        
        .no-results {
          color: var(--admin-text-muted);
          cursor: default;
        }
        
        .no-results:hover {
          background-color: transparent;
          color: var(--admin-text-muted);
        }
        
        .selected-package-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: var(--spacing-sm) var(--spacing-md);
          border: 1px solid var(--admin-border);
          border-radius: var(--radius-md);
          background-color: var(--admin-bg-secondary);
        }
        
        .selected-package-name {
          flex-grow: 1;
          font-weight: 500;
        }
        
        .btn-close-package {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0 var(--spacing-sm);
          font-size: 1.25rem;
          color: var(--admin-text-muted);
          transition: color var(--transition-base);
        }
        
        .btn-close-package:hover {
          color: var(--admin-danger);
        }
      `}</style>
    </AdminLayout>
  );
};

export default AddAccess;